import React, { useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import { getPicklistValues } from "../config/picklistConfig";

interface PicklistProps {
  fieldName: string;
  value: string;
  onChange: (e: { target: { name: string; value: string } }) => void;
  label?: string;
  objectName?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  options?: (string | { value: string; label: string })[];
}

const Picklist: React.FC<PicklistProps> = ({
  fieldName,
  value,
  onChange,
  label,
  objectName,
  required = false,
  disabled = false,
  error = false,
  helperText = "",
  options,
}) => {
  const { t, i18n } = useTranslation();

  const picklistOptions = useMemo(() => {
    if (options) {
      return options.map((opt) => {
        if (typeof opt === "string") {
          return { value: opt, label: opt };
        }
        return opt;
      });
    }

    const objName = objectName || 'caregiver';
    const englishList = getPicklistValues(objName, fieldName);
    const translatedList =
      i18n.language === "en"
        ? englishList
        : getPicklistValues(objName, `${fieldName}${i18n.language}`);

    return englishList.map((engVal: string, index: number) => {
      const labelVal =
        i18n.language !== "en" ? translatedList?.[index] ?? engVal : engVal;
      return { value: engVal, label: labelVal };
    });
  }, [options, objectName, fieldName, i18n.language]);

  const displayLabel =
    label ||
    fieldName
      .replace("__c", "")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .trim();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange({
      target: {
        name: fieldName,
        value: event.target.value,
      },
    });
  };

  return (
    <FormControl
      fullWidth
      error={error}
      required={required}
      variant="outlined"
    >
      <InputLabel id={`${fieldName}-label`}>{displayLabel}</InputLabel>
      <Select
        labelId={`${fieldName}-label`}
        id={fieldName}
        name={fieldName}
        value={value || ""}
        label={displayLabel}
        onChange={handleChange}
        disabled={disabled}
      >
        <MenuItem value="">
          <em>{t("form.selectAnOption")}</em>
        </MenuItem>
        {picklistOptions.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default Picklist;
